import { useToast } from "@repo/ui";
import { PasswordConfirmationModal } from "@/components/security/password-confirmation-modal";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { api, ApiClientError } from "@/lib/api/client";

interface DeleteReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportId: string;
  reportName: string;
  onSuccess?: () => void;
}

export function DeleteReportDialog({
  open,
  onOpenChange,
  reportId,
  reportName,
  onSuccess,
}: DeleteReportDialogProps) {
  const { toast } = useToast();
  const { requestApproval, isApprovalPending } = useApproval();

  const deleteWithToken = async (password: string, approvalToken?: string) => {
    try {
      await api.delete(`/reports/${reportId}`, {
        body: { password, approvalToken },
      });

      toast({
        title: "Report Deleted",
        description: `"${reportName}" has been deleted.`,
        variant: "success",
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => deleteWithToken(password, token),
          actionDescription: `Delete report "${reportName}"`,
        });
        return;
      }
      throw error;
    }
  };

  return (
    <PasswordConfirmationModal
      open={open && !isApprovalPending}
      onClose={() => onOpenChange(false)}
      onSuccess={(password) => deleteWithToken(password)}
      title="Delete Report - Password Required"
      description={`You are about to delete "${reportName}". This will permanently remove the file from storage. This action cannot be undone.`}
      actionLabel="Delete Report"
    />
  );
}
